import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cog, CheckCircle2, Loader2, Info, Eye, Download, CarFront } from "lucide-react";
import { adminApi } from "./api";

const AppleIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8.98-.2 1.92-.86 3.24-.77 1.58.13 2.77.75 3.56 1.9-3.27 1.96-2.5 6.27.53 7.5-.65 1.72-1.49 3.43-2.41 4.54zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);
const AndroidIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
  </svg>
);

const Toggle = ({ on, onChange, testId }) => (
  <button type="button" data-testid={testId} aria-pressed={on} onClick={() => onChange(!on)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-[#2340b5]" : "bg-[#12142b]/15"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const Radio = ({ checked, onChange, label, testId }) => (
  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#12142b]">
    <input type="radio" checked={checked} onChange={onChange} data-testid={testId} className="h-4 w-4 accent-[#2340b5]" />
    {label}
  </label>
);

const inputCls = "w-full rounded-xl border border-[#12142b]/12 bg-white px-3.5 py-2.5 text-sm text-[#12142b] outline-none focus:border-[#2340b5]/50 focus:ring-2 focus:ring-[#2340b5]/10";

function PlatformCard({ plat, label, tag, tagColor, Icon, cfg, onChange }) {
  const set = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid={`app-update-${plat}-card`}>
      <div className="mb-5 flex items-center justify-between">
        <p className="flex items-center gap-2 font-display text-base font-bold text-[#12142b]">
          <Icon className="h-5 w-5 text-[#12142b]" /> {label}
        </p>
        <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ backgroundColor: `${tagColor}14`, color: tagColor }}>{tag}</span>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[#12142b]">Enable {tag} update checks</span>
          <Toggle on={cfg.enabled} onChange={(v) => set("enabled", v)} testId={`app-update-${plat}-enabled`} />
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-[#12142b]">Update requirement</p>
          <div className="flex items-center gap-6">
            <Radio checked={cfg.mandatory} onChange={() => set("mandatory", true)} label="Mandatory update" testId={`app-update-${plat}-mandatory`} />
            <Radio checked={!cfg.mandatory} onChange={() => set("mandatory", false)} label="Optional update" testId={`app-update-${plat}-optional`} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-[#12142b]">Minimum supported version</span>
          <input value={cfg.min_version} onChange={(e) => set("min_version", e.target.value)} data-testid={`app-update-${plat}-min-version`}
            className={`${inputCls} w-40`} placeholder="2.0.0" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-[#12142b]">Latest available version</span>
          <input value={cfg.latest_version} onChange={(e) => set("latest_version", e.target.value)} data-testid={`app-update-${plat}-latest-version`}
            className={`${inputCls} w-40`} placeholder="2.1.0" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-[#12142b]">{plat === "ios" ? "App Store URL" : "Google Play Store URL"}</span>
          <input value={cfg.store_url} onChange={(e) => set("store_url", e.target.value)} data-testid={`app-update-${plat}-store-url`}
            className={`${inputCls} w-72`} placeholder="https://…" />
        </div>
        <div className="flex items-start gap-2 rounded-xl bg-[#2340b5]/6 px-3.5 py-2.5 text-xs text-[#2340b5]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {cfg.mandatory
            ? `${tag} users will be required to update if their version is below ${cfg.min_version || "…"}.`
            : `${tag} users will be notified to update if a newer version is available.`}
        </div>
      </div>
    </div>
  );
}

export default function AppUpdateSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminApi.get("/app-update").then((r) => setCfg(r.data)).catch(() => toast.error("Couldn't load app update settings"));
  }, []);

  const update = (mut) => { setCfg((c) => ({ ...c, ...mut })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { source: _src, ...body } = cfg;
      const { data } = await adminApi.put("/app-update", body);
      setCfg(data);
      setDirty(false);
      toast.success("App update settings saved");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="py-16 text-center text-sm text-[#565b73]">Loading app update settings…</div>;
  const { ios, android, message } = cfg;

  const SummaryChip = ({ plat, Icon, name }) => (
    <div className="flex items-center gap-4 rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm">
      <Icon className="h-9 w-9 text-[#12142b]" />
      <div>
        <p className="font-display text-base font-bold text-[#12142b]">{name}</p>
        <p className="font-display text-2xl font-black text-[#2340b5]" data-testid={`app-update-${plat}-summary-version`}>{cfg[plat].latest_version}</p>
        <p className="text-xs text-[#565b73]">Latest Version</p>
      </div>
      <span className={`ml-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${cfg[plat].enabled ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#12142b]/8 text-[#565b73]"}`}
        data-testid={`app-update-${plat}-summary-status`}>
        {cfg[plat].enabled ? <><CheckCircle2 className="h-3.5 w-3.5" /> Enabled</> : "Disabled"}
      </span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="app-update-section">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#2340b5]/10"><Cog className="h-5 w-5 text-[#2340b5]" /></span>
        <div>
          <h2 className="font-display text-xl font-black text-[#12142b]">App Update Management</h2>
          <p className="text-sm text-[#565b73]">Manage application versions and mandatory updates across iOS and Android.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SummaryChip plat="ios" Icon={AppleIcon} name="iOS" />
        <SummaryChip plat="android" Icon={AndroidIcon} name="Android" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PlatformCard plat="ios" label="Apple iOS Configuration" tag="iOS" tagColor="#2340b5" Icon={AppleIcon}
          cfg={ios} onChange={(v) => update({ ios: v })} />
        <PlatformCard plat="android" label="Google Android Configuration" tag="Android" tagColor="#16a34a" Icon={AndroidIcon}
          cfg={android} onChange={(v) => update({ android: v })} />
      </div>

      <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid="app-update-message-card">
        <p className="mb-4 flex items-center gap-2 font-display text-base font-bold text-[#12142b]">
          <Eye className="h-4 w-4 text-[#2340b5]" /> Update Notification Preview
        </p>
        <div className="flex flex-col gap-6 md:flex-row">
          {/* phone mock */}
          <div className="mx-auto w-56 shrink-0 rounded-[2rem] border-[6px] border-[#12142b] bg-white p-4 shadow-lg">
            <div className="flex flex-col items-center rounded-2xl border border-[#12142b]/8 px-4 py-6 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#2340b5]/10"><CarFront className="h-6 w-6 text-[#2340b5]" /></span>
              <p className="mt-3 font-display text-sm font-bold text-[#12142b]" data-testid="app-update-preview-title">{message.title}</p>
              <p className="mt-1 text-[11px] leading-snug text-[#565b73]" data-testid="app-update-preview-desc">{message.description}</p>
              <span className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#2340b5] py-2 text-xs font-bold text-white" data-testid="app-update-preview-btn">
                <Download className="h-3.5 w-3.5" /> {message.button_text}
              </span>
            </div>
          </div>
          <div className="flex-1 space-y-4">
            <p className="text-sm font-bold text-[#565b73]">Message Settings</p>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Title</label>
              <input value={message.title} onChange={(e) => update({ message: { ...message, title: e.target.value } })}
                data-testid="app-update-message-title" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Description</label>
              <textarea value={message.description} rows={3} onChange={(e) => update({ message: { ...message, description: e.target.value } })}
                data-testid="app-update-message-description" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Button Text</label>
              <input value={message.button_text} onChange={(e) => update({ message: { ...message, button_text: e.target.value } })}
                data-testid="app-update-message-button" className={inputCls} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="app-update-save" onClick={save} disabled={!dirty || saving}
          className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-6 py-3 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-40">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save App Update Settings
        </button>
      </div>
    </div>
  );
}
